import { useState, useEffect } from 'react';
import { SUPPORTED_LOCALES } from '../common';

/**
 * Configuration options for the useLocale hook
 */
interface UseLocaleOptions {
    /** Fallback locale if browser locale is unavailable */
    defaultLocale?: string;
    /** List of locales your application supports */
    supportedLocales?: string[];
}

/**
 * Locale information returned by the hook
 */
interface LocaleInfo {
    /** Full locale string (e.g., 'en-US') */
    fullLocale: string;
    /** Language code (e.g., 'en') */
    language: string;
    /** Country code (e.g., 'US') */
    country: string;
    /** List of browser language preferences */
    browserLanguages: string[];
    /** Whether the user's locale is directly supported */
    isSupported: boolean;
}

/**
 * Return type for the useLocale hook
 */
interface UseLocaleReturn extends LocaleInfo {
    /** Check if user's language matches the specified code */
    isLanguage: (languageCode: string) => boolean;
    /** Check if user's country matches the specified code */
    isCountry: (countryCode: string) => boolean;
    /** Check for an exact locale match (language AND country) */
    isExactLocale: (languageCode: string, countryCode: string) => boolean;
    /** Check if the locale matches one of multiple options */
    isOneOf: (locales: string[]) => boolean;
    /** Format a date according to the user's locale */
    formatDate: (date: Date, options?: Intl.DateTimeFormatOptions) => string;
    /** Format a number according to the user's locale */
    formatNumber: (
        number: number,
        options?: Intl.NumberFormatOptions
    ) => string;
}

/**
 * A custom React hook for detecting and working with the user's locale
 * @param options - Configuration options
 * @returns Locale information and utility functions
 */
const useLocale = (options: UseLocaleOptions = {}): UseLocaleReturn => {
    const defaultOptions: UseLocaleOptions = {
        defaultLocale: 'en-US',
        supportedLocales: [SUPPORTED_LOCALES.EN, SUPPORTED_LOCALES.HE],
    };

    const config = { ...defaultOptions, ...options };

    const [locale, setLocale] = useState<LocaleInfo>({
        fullLocale: config.defaultLocale || SUPPORTED_LOCALES.EN,
        language: (config.defaultLocale || SUPPORTED_LOCALES.EN).split('-')[0],
        country: (config.defaultLocale || SUPPORTED_LOCALES.EN).split('-')[1],
        browserLanguages: [],
        isSupported: true,
    });

    useEffect(() => {
        // Get browser language preferences
        const browserLanguages = navigator.languages || [navigator.language];

        // Get primary language preference
        const primaryLocale =
            navigator.language ||
            browserLanguages[0] ||
            config.defaultLocale ||
            SUPPORTED_LOCALES.EN;

        // Check if the locale is directly supported
        const isSupported = (config.supportedLocales || []).includes(
            primaryLocale
        );

        // Determine best locale to use (exact match or language match)
        let bestLocale = primaryLocale;
        if (
            !isSupported &&
            config.supportedLocales &&
            config.supportedLocales.length > 0
        ) {
            const languageCode = primaryLocale.split('-')[0];
            // Find first locale in supported list with matching language code
            const languageMatch = config.supportedLocales.find(
                (supportedLocale) =>
                    supportedLocale.startsWith(`${languageCode}-`)
            );
            if (languageMatch) {
                bestLocale = languageMatch;
            } else {
                bestLocale = config.defaultLocale || SUPPORTED_LOCALES.EN;
            }
        }

        // Split the locale into language and country components
        const [language, country] = bestLocale.split('-');

        setLocale({
            fullLocale: bestLocale,
            language: language?.toLowerCase() || '',
            country: country?.toUpperCase() || '',
            browserLanguages,
            isSupported: isSupported,
        });
        // Using a ref is the correct way to handle this - we only want to run this effect once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Check if user's language matches the specified code
     * @param languageCode - Language code to check (e.g., 'en', 'fr')
     * @returns True if language matches
     */
    const isLanguage = (languageCode: string): boolean => {
        return locale.language === languageCode.toLowerCase();
    };

    /**
     * Check if user's country matches the specified code
     * @param countryCode - Country code to check (e.g., 'US', 'FR')
     * @returns True if country matches
     */
    const isCountry = (countryCode: string): boolean => {
        return locale.country === countryCode.toUpperCase();
    };

    /**
     * Check for an exact locale match (language AND country)
     * @param languageCode - Language code to check
     * @param countryCode - Country code to check
     * @returns True if both language and country match
     */
    const isExactLocale = (
        languageCode: string,
        countryCode: string
    ): boolean => {
        return isLanguage(languageCode) && isCountry(countryCode);
    };

    /**
     * Check if the locale matches one of multiple options
     * @param locales - Array of locale codes (e.g., ['en-US', 'en-GB'])
     * @returns True if any locale matches
     */
    const isOneOf = (locales: string[]): boolean => {
        return locales.some((localeCode) => {
            const [langCode, countryCode] = localeCode.split('-');
            return isExactLocale(langCode, countryCode);
        });
    };

    /**
     * Format a date according to the user's locale
     * @param date - Date to format
     * @param options - Intl.DateTimeFormat options
     * @returns Formatted date string
     */
    const formatDate = (
        date: Date,
        options: Intl.DateTimeFormatOptions = {}
    ): string => {
        return new Intl.DateTimeFormat(locale.fullLocale, options).format(date);
    };

    /**
     * Format a number according to the user's locale
     * @param number - Number to format
     * @param options - Intl.NumberFormat options
     * @returns Formatted number string
     */
    const formatNumber = (
        number: number,
        options: Intl.NumberFormatOptions = {}
    ): string => {
        return new Intl.NumberFormat(locale.fullLocale, options).format(number);
    };

    return {
        ...locale,
        isLanguage,
        isCountry,
        isExactLocale,
        isOneOf,
        formatDate,
        formatNumber,
    };
};

export default useLocale;
